// Type definitions for constants
export interface MarketOption {
  value: string;
  title: string;
  key: string;
  country_code?: string;
  isRegionTotal?: boolean;
}

export interface RegionOption {
  value: string;
  title: string;
  key: string;
  children?: MarketOption[];
}

export interface RegionOption1 {
  region: string;
  children: MarketOption[];
}

export interface CountryInfo {
  code: string;
  name: string;
  region: string;
}

export interface RegionInfo {
  name: string;
  countries: string[];
  platforms: string[];
}

export interface PlatformOption {
  value: string;
  label: string;
}

export interface MetricsViewDataItem {
  key: string;
  region: string;
  market: string;
  platform: string;
  pet_score: string;
  total: string;
  av: string;
  core_av: string;
  combo: string;
  fixed_c: string;
  option_c: string;
  name: string;
  image: string;
}

export interface MetricsEvolutionDataItem {
  key: string;
  region: string;
  metric: string;
  jan: string;
  feb: string;
  mar: string;
  apr: string;
  may: string;
  jun: string;
  jul: string;
  aug: string;
  sep: string;
  oct: string;
  nov: string;
  dec: string;
}

export interface ChartBaseData {
  [regionOrMarket: string]: number[];
}

export interface TableTitleStyle {
  border: string;
  color: string;
  padding: number;
  height: string;
  fontSize: string;
  textAlign: string;
  fontWeight: number;
  minWidth: string;
  borderBottom: string;
}

// Platform and region mapping types
export type CountryCode = 
  | "AE" | "BE" | "DE" | "ES" | "FR" | "PK" | "SE" | "UK" | "US" | "ZA"
  | "BD" | "BG" | "KZ" | "LK" | "SA" | "TR" | "HK" | "TW" | "AU" | "NZ"
  | "AT" | "CH" | "CZ" | "DK" | "FI" | "GR" | "HU" | "IE" | "IT" | "LU"
  | "NL" | "NO" | "PL" | "PT" | "RO" | "ID" | "KH" | "MM" | "MY" | "PH"
  | "SG" | "TH" | "VN" | "CA";

export type RegionCode = "EME" | "EU" | "NA" | "AFRICA" | "ASIA" | "AUNZ" | "ASEAN" | "INDIA";

export type PlatformCode = 
  | "Total" | "deliveroo" | "noonfood" | "talabat" | "takeaway" | "ubereats"
  | "lieferando" | "wolt" | "glovo" | "justeat" | "foodpanda" | "foodora"
  | "doordash" | "grubhub" | "mrdfood";

export type MetricDisplayName =
  | "ONE Score" | "Availability" | "Full Core Availability" | "Combo Presence"
  | "Fixed Combo Presence" | "Option Combo Presence" | "Name Completeness" | "Image Presence" | "Correct Image" | "Total";

export type MetricKey =
  | "avg_score" | "avg_pct_availability" | "avg_pct_full_core_availability" | "avg_pct_combo"
  | "avg_pct_combo_fixed" | "avg_pct_combo_option"
  | "avg_pct_name_accuracy" | "avg_pct_high_name_accuracy"
  | "avg_pct_image_availability" | "avg_pct_any_image_availability"
  | "avg_pct_correct_image";

// Constants with proper typing
export const months: readonly string[] = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
] as const;

// Country to platforms mapping
export const COUNTRY_TO_PLATFORMS: Record<CountryCode, PlatformCode[]> = {
  "AE": ["Total", "deliveroo", "noonfood", "talabat"],
  "BE": ["Total", "deliveroo", "takeaway", "ubereats"],
  "DE": ["Total", "lieferando", "ubereats", "wolt"],
  "ES": ["Total", "glovo", "justeat", "ubereats"],
  "FR": ["Total", "deliveroo", "justeat", "ubereats"],
  "PK": ["Total", "foodpanda"],
  "SE": ["Total", "foodora", "ubereats", "wolt"],
  "UK": ["Total", "deliveroo", "justeat", "ubereats"],
  "US": ["Total", "doordash", "grubhub", "ubereats"],
  "ZA": ["Total", "mrdfood", "ubereats"],
  "BD": ["Total"],
  "BG": ["Total"],
  "KZ": ["Total"],
  "LK": ["Total"],
  "SA": ["Total"],
  "TR": ["Total"],
  "HK": ["Total"],
  "TW": ["Total"],
  "AU": ["Total"],
  "NZ": ["Total"],
  "AT": ["Total"],
  "CH": ["Total"],
  "CZ": ["Total"],
  "DK": ["Total"],
  "FI": ["Total"],
  "GR": ["Total"],
  "HU": ["Total"],
  "IE": ["Total"],
  "IT": ["Total"],
  "LU": ["Total"],
  "NL": ["Total"],
  "NO": ["Total"],
  "PL": ["Total"],
  "PT": ["Total"],
  "RO": ["Total"],
  "ID": ["Total"],
  "KH": ["Total"],
  "MM": ["Total"],
  "MY": ["Total"],
  "PH": ["Total"],
  "SG": ["Total"],
  "TH": ["Total"],
  "VN": ["Total"],
  "CA": ["Total"]
};

// Region to countries mapping
export const REGION_TO_COUNTRIES: Record<RegionCode, CountryCode[]> = {
  "EME": ["AE", "PK", "BD", "BG", "KZ", "LK", "SA", "TR"],
  "EU": ["BE", "DE", "ES", "FR", "SE", "UK", "AT", "CH", "CZ", "DK", "FI", "GR", "HU", "IE", "IT", "LU", "NL", "NO", "PL", "PT", "RO"],
  "NA": ["US", "CA"],
  "AFRICA": ["ZA"],
  "ASIA": ["HK", "TW"],
  "AUNZ": ["AU", "NZ"],
  "ASEAN": ["ID", "KH", "MM", "MY", "PH", "SG", "TH", "VN"],
  "INDIA": ["ID"] // Note: This seems to be a data inconsistency in the original
};

// Region to platforms mapping
export const REGION_TO_PLATFORMS: Record<RegionCode, PlatformCode[]> = {
  "EME": ["Total", "deliveroo", "noonfood", "talabat", "foodpanda"],
  "EU": ["Total", "deliveroo", "takeaway", "ubereats", "lieferando", "wolt", "glovo", "justeat", "foodora"],
  "NA": ["Total", "doordash", "grubhub", "ubereats"],
  "AFRICA": ["Total", "mrdfood", "ubereats"],
  "ASIA": ["Total"],
  "AUNZ": ["Total"],
  "ASEAN": ["Total"],
  "INDIA": ["Total"]
};

// Region information mapping
export const REGION_INFO: Record<RegionCode, RegionInfo> = {
  "EME": {
    name: "EME",
    countries: ["AE", "PK", "BD", "BG", "KZ", "LK", "SA", "TR"],
    platforms: ["Total", "deliveroo", "noonfood", "talabat", "foodpanda"]
  },
  "EU": {
    name: "EU", 
    countries: ["BE", "DE", "ES", "FR", "SE", "UK", "AT", "CH", "CZ", "DK", "FI", "GR", "HU", "IE", "IT", "LU", "NL", "NO", "PL", "PT", "RO"],
    platforms: ["Total", "deliveroo", "takeaway", "ubereats", "lieferando", "wolt", "glovo", "justeat", "foodora"]
  },
  "NA": {
    name: "NA",
    countries: ["US", "CA"],
    platforms: ["Total", "doordash", "grubhub", "ubereats"]
  },
  "AFRICA": {
    name: "AFRICA",
    countries: ["ZA"],
    platforms: ["Total", "mrdfood", "ubereats"]
  },
  "ASIA": {
    name: "ASIA",
    countries: ["HK", "TW"],
    platforms: ["Total"]
  },
  "AUNZ": {
    name: "AUNZ",
    countries: ["AU", "NZ"],
    platforms: ["Total"]
  },
  "ASEAN": {
    name: "ASEAN",
    countries: ["ID", "KH", "MM", "MY", "PH", "SG", "TH", "VN"],
    platforms: ["Total"]
  },
  "INDIA": {
    name: "INDIA",
    countries: ["ID"],
    platforms: ["Total"]
  }
};

// Country information mapping
export const COUNTRY_INFO: Record<CountryCode, CountryInfo> = {
  "AE": { code: "AE", name: "United Arab Emirates", region: "EME" },
  "BE": { code: "BE", name: "Belgium", region: "EU" },
  "DE": { code: "DE", name: "Germany", region: "EU" },
  "ES": { code: "ES", name: "Spain", region: "EU" },
  "FR": { code: "FR", name: "France", region: "EU" },
  "PK": { code: "PK", name: "Pakistan", region: "EME" },
  "SE": { code: "SE", name: "Sweden", region: "EU" },
  "UK": { code: "UK", name: "United Kingdom", region: "EU" },
  "US": { code: "US", name: "United States", region: "NA" },
  "ZA": { code: "ZA", name: "South Africa", region: "AFRICA" },
  "BD": { code: "BD", name: "Bangladesh", region: "EME" },
  "BG": { code: "BG", name: "Bulgaria", region: "EME" },
  "KZ": { code: "KZ", name: "Kazakhstan", region: "EME" },
  "LK": { code: "LK", name: "Sri Lanka", region: "EME" },
  "SA": { code: "SA", name: "Saudi Arabia", region: "EME" },
  "TR": { code: "TR", name: "Turkey", region: "EME" },
  "HK": { code: "HK", name: "Hong Kong", region: "ASIA" },
  "TW": { code: "TW", name: "Taiwan", region: "ASIA" },
  "AU": { code: "AU", name: "Australia", region: "AUNZ" },
  "NZ": { code: "NZ", name: "New Zealand", region: "AUNZ" },
  "AT": { code: "AT", name: "Austria", region: "EU" },
  "CH": { code: "CH", name: "Switzerland", region: "EU" },
  "CZ": { code: "CZ", name: "Czech Republic", region: "EU" },
  "DK": { code: "DK", name: "Denmark", region: "EU" },
  "FI": { code: "FI", name: "Finland", region: "EU" },
  "GR": { code: "GR", name: "Greece", region: "EU" },
  "HU": { code: "HU", name: "Hungary", region: "EU" },
  "IE": { code: "IE", name: "Ireland", region: "EU" },
  "IT": { code: "IT", name: "Italy", region: "EU" },
  "LU": { code: "LU", name: "Luxembourg", region: "EU" },
  "NL": { code: "NL", name: "Netherlands", region: "EU" },
  "NO": { code: "NO", name: "Norway", region: "EU" },
  "PL": { code: "PL", name: "Poland", region: "EU" },
  "PT": { code: "PT", name: "Portugal", region: "EU" },
  "RO": { code: "RO", name: "Romania", region: "EU" },
  "ID": { code: "ID", name: "Indonesia", region: "ASEAN" },
  "KH": { code: "KH", name: "Cambodia", region: "ASEAN" },
  "MM": { code: "MM", name: "Myanmar", region: "ASEAN" },
  "MY": { code: "MY", name: "Malaysia", region: "ASEAN" },
  "PH": { code: "PH", name: "Philippines", region: "ASEAN" },
  "SG": { code: "SG", name: "Singapore", region: "ASEAN" },
  "TH": { code: "TH", name: "Thailand", region: "ASEAN" },
  "VN": { code: "VN", name: "Vietnam", region: "ASEAN" },
  "CA": { code: "CA", name: "Canada", region: "NA" }
};

// Platform display names mapping
export const PLATFORM_DISPLAY_NAMES: Record<PlatformCode, string> = {
  "Total": "Total",
  "deliveroo": "Deliveroo",
  "noonfood": "Noon Food",
  "talabat": "Talabat",
  "takeaway": "Takeaway",
  "ubereats": "Uber Eats",
  "lieferando": "Lieferando",
  "wolt": "Wolt",
  "glovo": "Glovo",
  "justeat": "Just Eat",
  "foodpanda": "Foodpanda",
  "foodora": "Foodora",
  "doordash": "DoorDash",
  "grubhub": "Grubhub",
  "mrdfood": "Mr D Food"
};

// Platform color mapping for chart lines
export const PLATFORM_COLOR_MAPPING: Record<string, string> = {
  "deliveroo": "#00CCBC",
  "ubereats": "#06C167",
  "wolt": "#009DE0",
  "doordash": "#FF3008",
  "grubhub": "#F63440",
  "takeaway": "#FF8000",
  "lieferando": "#FF8000",
  "justeat": "#FF8000",
  "glovo": "#FFC244",
  "talabat": "#FF5A00",
  "noonfood": "#FEEE00",
  "foodpanda": "#D70F64",
  "foodora": "#D70F64",
  "mrdfood": "#E23744",
};

// Utility functions with proper typing
export const getAvailablePlatformsForCountry = (countryCode: CountryCode): PlatformCode[] => {
  return COUNTRY_TO_PLATFORMS[countryCode] || [];
};

export const getAvailablePlatformsForRegion = (regionCode: RegionCode): PlatformCode[] => {
  return REGION_TO_PLATFORMS[regionCode] || [];
};

export const getCountriesForRegion = (regionCode: RegionCode): CountryCode[] => {
  return REGION_TO_COUNTRIES[regionCode] || [];
};

export const isPlatformAvailableForCountry = (countryCode: CountryCode, platform: PlatformCode): boolean => {
  const availablePlatforms = COUNTRY_TO_PLATFORMS[countryCode] || [];
  return availablePlatforms.includes(platform);
};

// Mock data with proper typing
export const metricsViewData: MetricsViewDataItem[] = [
  {
    key: "1",
    region: "Global",
    market: "Total",
    platform: "Total",
    pet_score: "78",
    total: "78",
    av: "78%",
    core_av: "78%",
    combo: "55%",
    fixed_c: "96%",
    option_c: "96%",
    name: "96%",
    image: "78%",
  },
  {
    key: "2",
    region: "Europe",
    market: "Total",
    platform: "Total",
    pet_score: "100",
    total: "100",
    av: "96%",
    core_av: "96%",
    combo: "78%",
    fixed_c: "96%",
    option_c: "78%",
    name: "55%",
    image: "100%",
  },
  {
    key: "3",
    region: "Europe",
    market: "Netherlands",
    platform: "Thuisbezorgd",
    pet_score: "60",
    total: "60",
    av: "96%",
    core_av: "96%",
    combo: "96%",
    fixed_c: "78%",
    option_c: "100%",
    name: "96%",
    image: "100%",
  },
  {
    key: "4",
    region: "Europe",
    market: "Belgium",
    platform: "Uber Eats",
    pet_score: "78",
    total: "78",
    av: "78%",
    core_av: "78%",
    combo: "78%",
    fixed_c: "78%",
    option_c: "55%",
    name: "78%",
    image: "78%",
  },
  {
    key: "5",
    region: "North America",
    market: "Total",
    platform: "Total",
    pet_score: "100",
    total: "100",
    av: "55%",
    core_av: "55%",
    combo: "55%",
    fixed_c: "78%",
    option_c: "78%",
    name: "96%",
    image: "55%",
  },
  {
    key: "6",
    region: "North America",
    market: "USA",
    platform: "Grubhub",
    pet_score: "78",
    total: "78",
    av: "78%",
    core_av: "78%",
    combo: "78%",
    fixed_c: "55%",
    option_c: "96%",
    name: "78%",
    image: "96%",
  },
  {
    key: "7",
    region: "North America",
    market: "Canada",
    platform: "Skip",
    pet_score: "60",
    total: "60",
    av: "96%",
    core_av: "96%",
    combo: "96%",
    fixed_c: "78%",
    option_c: "78%",
    name: "96%",
    image: "78%",
  },
];

export const metricsEvolutionData: MetricsEvolutionDataItem[] = [
  {
    key: "1",
    region: "Global",
    metric: "PET",
    jan: "78",
    feb: "78",
    mar: "78",
    apr: "78",
    may: "78",
    jun: "78",
    jul: "78",
    aug: "78",
    sep: "78",
    oct: "78",
    nov: "78",
    dec: "78",
  },
  {
    key: "2",
    region: "Europe",
    metric: "PET",
    jan: "60",
    feb: "60",
    mar: "60",
    apr: "60",
    may: "60",
    jun: "60",
    jul: "60",
    aug: "60",
    sep: "60",
    oct: "60",
    nov: "60",
    dec: "60",
  },
  {
    key: "3",
    region: "Asia",
    metric: "PET",
    jan: "78",
    feb: "78",
    mar: "78",
    apr: "78",
    may: "78",
    jun: "78",
    jul: "78",
    aug: "78",
    sep: "78",
    oct: "78",
    nov: "78",
    dec: "78",
  },
  {
    key: "4",
    region: "North America",
    metric: "PET",
    jan: "100",
    feb: "100",
    mar: "100",
    apr: "100",
    may: "100",
    jun: "100",
    jul: "100",
    aug: "100",
    sep: "100",
    oct: "100",
    nov: "100",
    dec: "100",
  },
  {
    key: "5",
    region: "South America",
    metric: "PET",
    jan: "78",
    feb: "78",
    mar: "78",
    apr: "78",
    may: "78",
    jun: "78",
    jul: "78",
    aug: "78",
    sep: "78",
    oct: "78",
    nov: "78",
    dec: "78",
  },
  {
    key: "6",
    region: "ASEAN",
    metric: "PET",
    jan: "60",
    feb: "60",
    mar: "60",
    apr: "60",
    may: "60",
    jun: "60",
    jul: "60",
    aug: "60",
    sep: "60",
    oct: "60",
    nov: "60",
    dec: "60",
  },
];

// Chart base data with proper typing
export const chartBaseData: ChartBaseData = {
  'Global': [60, 50, 55, 65, 70, 60, 50, 60, 70, 55, 60, 60],
  'Global Market': [60, 50, 55, 65, 70, 60, 50, 60, 70, 55, 60, 60],
  'Global Overall': [60, 50, 55, 65, 70, 60, 50, 60, 70, 55, 60, 60],
  'Europe': [50, 45, 60, 80, 65, 40, 30, 85, 55, 30, 45, 60],
  'Asia': [45, 50, 40, 55, 65, 45, 25, 70, 60, 65, 50, 45],
  'North America': [50, 25, 25, 35, 70, 20, 30, 45, 80, 45, 60, 60],
  'South America': [55, 20, 20, 40, 50, 35, 20, 50, 65, 35, 55, 50],
  'ASEAN': [45, 15, 10, 20, 25, 10, 20, 35, 55, 40, 45, 45],
  'ASEAN Overall': [45, 15, 10, 20, 25, 10, 20, 35, 55, 40, 45, 45],
  'Asia Overall': [45, 50, 40, 55, 65, 45, 25, 70, 60, 65, 50, 45],
  'AUNZ': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'AUNZ Overall': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'EME': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'EME Overall': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'EU': [45, 40, 35, 40, 50, 45, 40, 45, 55, 50, 45, 50],
  'EU Overall': [45, 40, 35, 40, 50, 45, 40, 45, 55, 50, 45, 50],
  'India': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'India Overall': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'NA': [55, 50, 45, 50, 60, 55, 50, 55, 65, 60, 55, 60],
  'NA Overall': [55, 50, 45, 50, 60, 55, 50, 55, 65, 60, 55, 60],
  'Netherlands': [40, 10, 10, 15, 10, 10, 15, 10, 20, 15, 40, 45],
  'Belgium': [35, 35, 30, 55, 40, 30, 40, 45, 55, 30, 40, 45],
  'Cambodia': [30, 25, 35, 40, 45, 30, 25, 30, 40, 35, 40, 45],
  'Malaysia': [50, 45, 40, 45, 55, 50, 45, 50, 60, 55, 50, 55],
  'Singapore': [65, 60, 55, 65, 70, 65, 60, 65, 75, 70, 65, 70],
  'Vietnam': [35, 30, 25, 30, 35, 30, 25, 30, 40, 35, 30, 35],
  'Thailand': [55, 50, 45, 50, 55, 50, 45, 50, 60, 55, 50, 55],
  'Myanmar': [25, 20, 15, 20, 30, 25, 20, 25, 35, 30, 25, 30],
  'Philippines': [40, 35, 30, 35, 45, 40, 35, 40, 50, 45, 40, 45],
  'Indonesia': [45, 40, 35, 40, 50, 45, 40, 45, 55, 50, 45, 50],
  'China': [70, 65, 60, 65, 75, 70, 65, 70, 80, 75, 70, 75],
  'Japan': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'Korea': [55, 50, 45, 50, 60, 55, 50, 55, 65, 60, 55, 60],
  'Australia': [65, 60, 55, 60, 70, 65, 60, 65, 75, 70, 65, 70],
  'New Zealand': [55, 50, 45, 50, 60, 55, 50, 55, 65, 60, 55, 60],
  'UK': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'Germany': [65, 60, 55, 60, 70, 65, 60, 65, 75, 70, 65, 70],
  'France': [50, 45, 40, 45, 55, 50, 45, 50, 60, 55, 50, 55],
  'Delhi': [55, 50, 45, 50, 60, 55, 50, 55, 65, 60, 55, 60],
  'Mumbai': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'USA': [60, 55, 50, 55, 65, 60, 55, 60, 70, 65, 60, 65],
  'Canada': [55, 50, 45, 50, 60, 55, 50, 55, 65, 60, 55, 60]
};

// Chart configuration
export const defaultChartLegends: readonly string[] = [
  'AFRICA', 'NA', 'EU', 'EME'
] as const;

export const chartColors: readonly string[] = [
  '#E91E63', '#4CAF50', '#2196F3', '#FF5722',
  '#9C27B0', '#FF9800', '#795548', '#607D8B',
  '#F44336', '#8BC34A', '#03A9F4', '#FF9600',
  '#673AB7', '#FFC107', '#3F51B5', '#009688',
  '#CDDC39', '#FF5252', '#448AFF', '#69F0AE'
] as const;

// Per-metric line-dash patterns (SVG strokeDasharray) used when several metrics
// are overlaid on the Performance Trend chart for multiple markets: color encodes
// the market, this pattern encodes the metric. Index 0 is solid. Indexed by the
// metric's position in the selection, wrapping with modulo. Must have at least as
// many entries as METRIC_KV_MAP has metrics, so selecting every metric across
// multiple markets never reuses a pattern (which would re-collide a market's lines).
export const METRIC_DASH_PATTERNS: readonly string[] = [
  '',              // solid
  '8 4',           // dashed
  '2 4',           // dotted
  '12 4 2 4',      // dash-dot
  '16 6',          // long dash
  '2 4 8 4',       // dot-dash
  '10 3 2 3 2 3',  // dash-dot-dot
  '6 6',           // even dash
  '14 4 4 4',      // long + short
  '4 8',           // sparse dash
] as const;

export const allMetrics: readonly MetricDisplayName[] = [
  "ONE Score",
  "Availability",
  "Full Core Availability",
  "Combo Presence",
  "Fixed Combo Presence",
  "Option Combo Presence",
  "Name Completeness",
  "Image Presence",
  "Correct Image"
] as const;

// Find the Region/Market tree node for a given country code (e.g. "AU").
export const findMarketByCountry = (
  regionOptions: RegionOption[],
  countryCode: string
): MarketOption | undefined => {
  for (const region of regionOptions) {
    const market = region.children?.find(
      (child) => child.country_code === countryCode
    );
    if (market) return market;
  }
  return undefined;
};

// Table styling
export const tableTitleStyle: TableTitleStyle = {
  border: "none",
  color: "white",
  padding: 0,
  height: "10px",
  fontSize: "0.5rem",
  textAlign: "center",
  fontWeight: 500,
  minWidth: "100%",
  borderBottom: "1px solid #f4f4f5",
};

// Region and market options with proper typing
export const regionMarketOptions: RegionOption[] = [
  {
    value: "global",
    title: "Global",
    key: "global",
  },
  {
    value: "africa",
    title: "AFRICA",
    key: "africa",
    children: [
      {
        value: "africa_total",
        title: "AFRICA Aggregated",
        key: "africa_total",
        isRegionTotal: true,
      },
      {
        value: "south africa",
        title: "South Africa",
        key: "south_africa",
        country_code: "ZA",
      },
    ],
  },
  {
    value: "eme",
    title: "EME",
    key: "eme",
    children: [
      {
        value: "eme_total",
        title: "EME Aggregated",
        key: "eme_total",
        isRegionTotal: true,
      },
      {
        value: "united arab emirates",
        title: "United Arab Emirates",
        key: "united_arab_emirates",
        country_code: "AE",
      },
      {
        value: "bangladesh",
        title: "Bangladesh",
        key: "bangladesh",
        country_code: "BD",
      },
      {
        value: "bulgaria",
        title: "Bulgaria",
        key: "bulgaria",
        country_code: "BG",
      },
      {
        value: "kazakhstan",
        title: "Kazakhstan",
        key: "kazakhstan",
        country_code: "KZ",
      },
      {
        value: "sri lanka",
        title: "Sri Lanka",
        key: "sri_lanka",
        country_code: "LK",
      },
      {
        value: "pakistan",
        title: "Pakistan",
        key: "pakistan",
        country_code: "PK",
      },
      {
        value: "saudi arabia",
        title: "Saudi Arabia",
        key: "saudi_arabia",
        country_code: "SA",
      },
      {
        value: "turkey",
        title: "Turkey",
        key: "turkey",
        country_code: "TR",
      },
    ],
  },
  {
    value: "asia",
    title: "ASIA",
    key: "asia",
    children: [
      {
        value: "asia_total",
        title: "ASIA Aggregated",
        key: "asia_total",
        isRegionTotal: true,
      },
      {
        value: "hong kong",
        title: "Hong Kong",
        key: "hong_kong",
        country_code: "HK",
      },
      {
        value: "taiwan",
        title: "Taiwan",
        key: "taiwan",
        country_code: "TW",
      },
    ],
  },
  {
    value: "aunz",
    title: "AUNZ",
    key: "aunz",
    children: [
      {
        value: "aunz_total",
        title: "AUNZ Aggregated",
        key: "aunz_total",
        isRegionTotal: true,
      },
      {
        value: "australia",
        title: "Australia",
        key: "australia",
        country_code: "AU",
      },
      {
        value: "new zealand",
        title: "New Zealand",
        key: "new_zealand",
        country_code: "NZ",
      },
    ],
  },
  {
    value: "eu",
    title: "EU",
    key: "eu",
    children: [
      {
        value: "eu_total",
        title: "EU Aggregated",
        key: "eu_total",
        isRegionTotal: true,
      },
      {
        value: "austria",
        title: "Austria",
        key: "austria",
        country_code: "AT",
      },
      {
        value: "belgium",
        title: "Belgium",
        key: "belgium",
        country_code: "BE",
      },
      {
        value: "switzerland",
        title: "Switzerland",
        key: "switzerland",
        country_code: "CH",
      },
      {
        value: "czech republic",
        title: "Czech Republic",
        key: "czech_republic",
        country_code: "CZ",
      },
      {
        value: "germany",
        title: "Germany",
        key: "germany",
        country_code: "DE",
      },
      {
        value: "denmark",
        title: "Denmark",
        key: "denmark",
        country_code: "DK",
      },
      {
        value: "spain",
        title: "Spain",
        key: "spain",
        country_code: "ES",
      },
      {
        value: "finland",
        title: "Finland",
        key: "finland",
        country_code: "FI",
      },
      {
        value: "france",
        title: "France",
        key: "france",
        country_code: "FR",
      },
      {
        value: "greece",
        title: "Greece",
        key: "greece",
        country_code: "GR",
      },
      {
        value: "hungary",
        title: "Hungary",
        key: "hungary",
        country_code: "HU",
      },
      {
        value: "ireland",
        title: "Ireland",
        key: "ireland",
        country_code: "IE",
      },
      {
        value: "italy",
        title: "Italy",
        key: "italy",
        country_code: "IT",
      },
      {
        value: "luxembourg",
        title: "Luxembourg",
        key: "luxembourg",
        country_code: "LU",
      },
      {
        value: "netherlands",
        title: "Netherlands",
        key: "netherlands",
        country_code: "NL",
      },
      {
        value: "norway",
        title: "Norway",
        key: "norway",
        country_code: "NO",
      },
      {
        value: "poland",
        title: "Poland",
        key: "poland",
        country_code: "PL",
      },
      {
        value: "portugal",
        title: "Portugal",
        key: "portugal",
        country_code: "PT",
      },
      {
        value: "romania",
        title: "Romania",
        key: "romania",
        country_code: "RO",
      },
      {
        value: "sweden",
        title: "Sweden",
        key: "sweden",
        country_code: "SE",
      },
      {
        value: "united kingdom",
        title: "United Kingdom",
        key: "united_kingdom",
        country_code: "UK",
      },
    ],
  },
  {
    value: "asean",
    title: "ASEAN",
    key: "asean",
    children: [
      {
        value: "asean_total",
        title: "ASEAN Aggregated",
        key: "asean_total",
        isRegionTotal: true,
      },
      {
        value: "indonesia",
        title: "Indonesia",
        key: "indonesia",
        country_code: "ID",
      },
      {
        value: "cambodia",
        title: "Cambodia",
        key: "cambodia",
        country_code: "KH",
      },
      {
        value: "myanmar",
        title: "Myanmar",
        key: "myanmar",
        country_code: "MM",
      },
      {
        value: "malaysia",
        title: "Malaysia",
        key: "malaysia",
        country_code: "MY",
      },
      {
        value: "philippines",
        title: "Philippines",
        key: "philippines",
        country_code: "PH",
      },
      {
        value: "singapore",
        title: "Singapore",
        key: "singapore",
        country_code: "SG",
      },
      {
        value: "thailand",
        title: "Thailand",
        key: "thailand",
        country_code: "TH",
      },
      {
        value: "vietnam",
        title: "Vietnam",
        key: "vietnam",
        country_code: "VN",
      },
    ],
  },
  {
    value: "na",
    title: "NA",
    key: "na",
    children: [
      {
        value: "na_total",
        title: "NA Aggregated",
        key: "na_total",
        isRegionTotal: true,
      },
      {
        value: "canada",
        title: "Canada",
        key: "canada",
        country_code: "CA",
      },
      {
        value: "united states",
        title: "United States",
        key: "united_states",
        country_code: "US",
      },
    ],
  },
  {
    value: "india",
    title: "INDIA",
    key: "india",
    children: [
      {
        value: "india_total",
        title: "INDIA Aggregated",
        key: "india_total",
        isRegionTotal: true,
      },
      {
        value: "indonesia",
        title: "Indonesia",
        key: "indonesia",
        country_code: "ID",
      },
    ],
  },
];

// Platform mapping with proper typing
export const PLATFORM_DISPLAY_MAPPING: Record<PlatformCode, string> = {
  "Total": "All Platforms",
  "deliveroo": "Deliveroo",
  "noonfood": "Noon Food",
  "talabat": "Talabat",
  "takeaway": "Takeaway",
  "ubereats": "Uber Eats",
  "lieferando": "Lieferando",
  "wolt": "Wolt",
  "glovo": "Glovo",
  "justeat": "Just Eat",
  "foodpanda": "Foodpanda",
  "foodora": "Foodora",
  "doordash": "DoorDash",
  "grubhub": "Grubhub",
  "mrdfood": "Mr D Food"
};

// Reverse mapping
export const DISPLAY_TO_PLATFORM_MAPPING: Record<string, PlatformCode> = Object.fromEntries(
  Object.entries(PLATFORM_DISPLAY_MAPPING).map(([key, value]) => [value, key as PlatformCode])
) as Record<string, PlatformCode>;

// Platform utility functions with proper typing
export const getPlatformsForCountries = (selectedCountries: CountryCode[]): PlatformOption[] => {
  if (!selectedCountries || selectedCountries.length === 0) {
    return Object.values(PLATFORM_DISPLAY_MAPPING).map(displayName => ({
      value: DISPLAY_TO_PLATFORM_MAPPING[displayName],
      label: displayName
    }));
  }

  const availablePlatforms = new Set<PlatformCode>();
  selectedCountries.forEach(countryCode => {
    if (COUNTRY_TO_PLATFORMS[countryCode]) {
      COUNTRY_TO_PLATFORMS[countryCode].forEach(platform => {
        availablePlatforms.add(platform);
      });
    }
  });

  return Array.from(availablePlatforms).map(platform => ({
    value: platform,
    label: PLATFORM_DISPLAY_MAPPING[platform] || platform
  }));
};

export const getPlatformsForRegions = (selectedRegions: RegionCode[]): PlatformOption[] => {
  if (!selectedRegions || selectedRegions.length === 0) {
    return Object.values(PLATFORM_DISPLAY_MAPPING).map(displayName => ({
      value: DISPLAY_TO_PLATFORM_MAPPING[displayName],
      label: displayName
    }));
  }

  const availablePlatforms = new Set<PlatformCode>();
  selectedRegions.forEach(region => {
    if (REGION_TO_PLATFORMS[region]) {
      REGION_TO_PLATFORMS[region].forEach(platform => {
        availablePlatforms.add(platform);
      });
    }
  });

  return Array.from(availablePlatforms).map(platform => ({
    value: platform,
    label: PLATFORM_DISPLAY_MAPPING[platform] || platform
  }));
};

// Platform options with proper typing
export const platformOptions: PlatformOption[] = [
  { value: 'Total', label: 'All Platforms' },
  { value: 'deliveroo', label: 'Deliveroo' },
  { value: 'noonfood', label: 'Noon Food' },
  { value: 'talabat', label: 'Talabat' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'ubereats', label: 'Uber Eats' },
  { value: 'lieferando', label: 'Lieferando' },
  { value: 'wolt', label: 'Wolt' },
  { value: 'glovo', label: 'Glovo' },
  { value: 'justeat', label: 'Just Eat' },
  { value: 'foodpanda', label: 'Foodpanda' },
  { value: 'foodora', label: 'Foodora' },
  { value: 'doordash', label: 'DoorDash' },
  { value: 'grubhub', label: 'Grubhub' },
  { value: 'mrdfood', label: 'Mr D Food' },
];

// Restaurant options
export const restaurantOptions: PlatformOption[] = [
  { value: 'both', label: 'Both' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'non-restaurant', label: 'Non Restaurant' },
];

// Maps a display name to the API metric key. Deliberately partial: "Total" is a
// `MetricDisplayName` with no metric of its own, and every consumer already falls
// back to "avg_score" when a lookup misses.
export const METRIC_KV_MAP: Partial<Record<MetricDisplayName, MetricKey>> = {
  "ONE Score": "avg_score",
  "Availability": "avg_pct_availability",
  "Full Core Availability": "avg_pct_full_core_availability",
  "Combo Presence": "avg_pct_combo",
  "Fixed Combo Presence": "avg_pct_combo_fixed",
  "Option Combo Presence": "avg_pct_combo_option",
  "Name Completeness": "avg_pct_high_name_accuracy",
  "Image Presence": "avg_pct_any_image_availability",
  "Correct Image": "avg_pct_correct_image",
}; 

// Country code to country name mapping
export const COUNTRY_CODE_TO_NAME = {
  "AE": "United Arab Emirates",
  "BD": "Bangladesh", 
  "BG": "Bulgaria",
  "KZ": "Kazakhstan",
  "LK": "Sri Lanka",
  "PK": "Pakistan",
  "SA": "Saudi Arabia",
  "TR": "Turkey",
  "BE": "Belgium",
  "DE": "Germany",
  "ES": "Spain",
  "FR": "France",
  "SE": "Sweden",
  "UK": "United Kingdom",
  "AT": "Austria",
  "CH": "Switzerland",
  "CZ": "Czech Republic",
  "DK": "Denmark",
  "FI": "Finland",
  "GR": "Greece",
  "HU": "Hungary",
  "IE": "Ireland",
  "IT": "Italy",
  "LU": "Luxembourg",
  "NL": "Netherlands",
  "NO": "Norway",
  "PL": "Poland",
  "PT": "Portugal",
  "RO": "Romania",
  "US": "United States",
  "CA": "Canada",
  "ZA": "South Africa",
  "HK": "Hong Kong",
  "TW": "Taiwan",
  "AU": "Australia",
  "NZ": "New Zealand",
  "ID": "Indonesia",
  "KH": "Cambodia",
  "MM": "Myanmar",
  "MY": "Malaysia",
  "PH": "Philippines",
  "SG": "Singapore",
  "TH": "Thailand",
  "VN": "Vietnam",
  "IN": "India"
};

// Region key mapping for consistent naming
export const REGION_KEY_MAPPING = {
  "europe": "eu",
  "north_america": "na",
  "eme": "eme",
  "africa": "africa",
  "asia": "asia",
  "aunz": "aunz",
  "asean": "asean",
  "india": "india"
};

export const COUNTRY_TO_COLOR_MAPPING = [
  // 🌍 AFRICA
  { "country": "South Africa", "code": "ZA", "color": "#A84000" },
  { "country": "AFRICA Aggregated", "code": "africa_total", "color": "#B24D1A" },
  // 🌏 ASIA
  { "country": "Hong Kong", "code": "HK", "color": "#158872" },
  { "country": "Taiwan", "code": "TW", "color": "#0D5C58" },
  { "country": "ASIA Aggregated", "code": "asia_total", "color": "#275A7A" },
  // 🌐 EME
  { "country": "United Arab Emirates", "code": "AE", "color": "#99241F" },
  { "country": "Bangladesh", "code": "BD", "color": "#208C4D" },
  { "country": "Bulgaria", "code": "BG", "color": "#701C16" },
  { "country": "Kazakhstan", "code": "KZ", "color": "#A6830A" },
  { "country": "Sri Lanka", "code": "LK", "color": "#C6893E" },
  { "country": "Pakistan", "code": "PK", "color": "#32A469" },
  { "country": "Saudi Arabia", "code": "SA", "color": "#135631" },
  { "country": "Turkey", "code": "TR", "color": "#B52A25" },
  { "country": "EME Aggregated", "code": "eme_total", "color": "#51215F" },
  // 🇦🇺 AUNZ
  { "country": "Australia", "code": "AU", "color": "#BBAE0C" },
  { "country": "New Zealand", "code": "NZ", "color": "#0E6F63" },
  { "country": "AUNZ Aggregated", "code": "aunz_total", "color": "#0C594E" },
  // 🇪🇺 EU
  { "country": "Austria", "code": "AT", "color": "#992E26" },
  { "country": "Belgium", "code": "BE", "color": "#CABF37" },
  { "country": "Switzerland", "code": "CH", "color": "#84261F" },
  { "country": "Czech Republic", "code": "CZ", "color": "#1E5B8A" },
  { "country": "Germany", "code": "DE", "color": "#12191F" },
  { "country": "Denmark", "code": "DK", "color": "#731F16" },
  { "country": "Spain", "code": "ES", "color": "#B6611A" },
  { "country": "Finland", "code": "FI", "color": "#236298" },
  { "country": "France", "code": "FR", "color": "#495D6A" },
  { "country": "Greece", "code": "GR", "color": "#248EDD" },
  { "country": "Hungary", "code": "HU", "color": "#53B186" },
  { "country": "Ireland", "code": "IE", "color": "#21A351" },
  { "country": "Italy", "code": "IT", "color": "#04512C" },
  { "country": "Luxembourg", "code": "LU", "color": "#64B3E5" },
  { "country": "Netherlands", "code": "NL", "color": "#3E657E" },
  { "country": "Norway", "code": "NO", "color": "#034D84" },
  { "country": "Poland", "code": "PL", "color": "#E0231A" },
  { "country": "Portugal", "code": "PT", "color": "#0C594E" },
  { "country": "Romania", "code": "RO", "color": "#982019" },
  { "country": "Sweden", "code": "SE", "color": "#CABF37" },
  { "country": "United Kingdom", "code": "UK", "color": "#2C4161" },
  { "country": "EU Aggregated", "code": "eu_total", "color": "#5A5F5F" },
  // 🌴 ASEAN
  { "country": "Indonesia", "code": "ID", "color": "#A32A24" },
  { "country": "Cambodia", "code": "KH", "color": "#1C5D8D" },
  { "country": "Myanmar", "code": "MM", "color": "#D19A14" },
  { "country": "Malaysia", "code": "MY", "color": "#98231F" },
  { "country": "Philippines", "code": "PH", "color": "#C7D1D1" },
  { "country": "Singapore", "code": "SG", "color": "#C7443A" },
  { "country": "Thailand", "code": "TH", "color": "#195885" },
  { "country": "Vietnam", "code": "VN", "color": "#A95C57" },
  { "country": "ASEAN Aggregated", "code": "asean_total", "color": "#208C4D" },
  // 🇺🇸 NA
  { "country": "Canada", "code": "CA", "color": "#C62C24" },
  { "country": "United States", "code": "US", "color": "#2C4161" },
  { "country": "NA Aggregated", "code": "na_total", "color": "#404E63" },
  // 🇮🇳 INDIA
  { "country": "Indonesia (India group)", "code": "ID", "color": "#A64000" },
  { "country": "INDIA Aggregated", "code": "india_total", "color": "#C45B19" }
]